/*Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/


import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  SvgIcon,
  Table,
  Spinner,
  Button,
  Modal,
  SpanWithData
} from '../'

import {
  decodeBase64,
  sortDates
} from '../../utils'

class IdentityMessages extends React.Component {
  static propTypes = {
    identity: PropTypes.string,
    orderBy: PropTypes.string,
    actionColumn: PropTypes.shape({
      name: PropTypes.string.isRequired,
      render: PropTypes.func.isRequired
    }),
    className: PropTypes.string
  }

  static defaultProps = {
    orderBy: '-created_at'
  }

  constructor(props) {
    super(props)

    this.state = {
      selectedMessage: null,
      replyToMessage: false
    }
  }

  getTableRows = () => {
    let messages = [
      ...this.props.data.inboundMessages.edges,
      ...this.props.data.outboundMessages.edges
    ].sort((a,b) => {
      let sortDescendingly = this.props.orderBy[0] === '-'
      let orderByField = sortDescendingly
        ? this.props.orderBy.substr(1).replace('_at', 'At')
        : this.props.orderBy.replace('_at', 'At')

      return sortDescendingly
        ? sortDates(a.node[orderByField], b.node[orderByField], true)
        : sortDates(a.node[orderByField], b.node[orderByField], false)
    })

    return (
      messages.map((message, index) => (
        <MessageTableItem
          message={message.node}
          identity={this.props.identity}
          actionColumn={this.props.actionColumn}
          expandMessage={this.state.selectedMessage === message.node.id}
          onExpandClick={messageId => this.setState({ selectedMessage: messageId })}
          onExpandCloseClick={() => this.setState({ selectedMessage: null })}
          key={`message_${index}`} />
      ))
    )
  }

  render() {
    if (this.props.data.loading) {
      return <div className="slds-spinner_container">
        <Spinner className='slds-is-relative' />
      </div>
    }

    if (this.props.data.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    let columns = ["Status", "Direction", "Type", "Scheduled", "Sent", "Message"]
    if (this.props.actionColumn) {
      columns.push(this.props.actionColumn.name)
    }

    return (
      <div className={this.props.className ? this.props.className : ''}>
        {this.props.data.inboundMessages.edges.length === 0 &&
          this.props.data.outboundMessages.edges.length === 0
          ? <React.Fragment>
              <SvgIcon
                variant='utility'
                symbol='info'
                size='small'
                colour='light'
                className='slds-m-right_medium'
              />
              No messages.
            </React.Fragment>
          : <Table
              columns={columns}
              rows={this.getTableRows()}
              objectRows={true}
            />
        }
      </div>
    )
  }
}

const MessageTableItem = ({ message, identity, actionColumn, expandMessage, replyToMessage, onExpandClick, onExpandCloseClick }) => (
  <tr onClick={() => onExpandClick(message.id)} className='clickable'>
    <td>
      <SpanWithData forField="MessageStatus" searchKey={message.status} />
    </td>
    <td>
      <SpanWithData forField="MessageDirection" searchKey={message.direction} />
    </td>
    <td>
      {message.messageType
        ? <SpanWithData forField="MessageMessageType" searchKey={message.messageType} />
        : "Unknown"
      }
    </td>
    <td>
      {message.scheduledAt
        ? moment().isSame(moment(message.scheduledAt), 'day')
          ? moment(message.scheduledAt).format('hh:mm a')
          : moment(message.scheduledAt).format('lll')
        : "Unknown"
      }
    </td>
    <td>
      {moment().isSame(moment(message.timestamp), 'day')
        ? moment(message.timestamp).format('hh:mm a')
        : moment(message.timestamp).format('lll')
      }
    </td>
    <td>
      {expandMessage
        ? <Modal
            body={
              <p style={{ whiteSpace: 'normal' }}>
                {message.content}
              </p>
            }
            footer={
              <Button
                type='brand'
                onClick={(e) => {
                  e.stopPropagation()  // prevents click on outer <tr> to be captured
                  onExpandCloseClick()
                }}
                className='slds-align_absolute-center'>
                Close
              </Button>
            }
          />
        : <div
            className='slds-truncate'
            style={{ width: '200px' }}>
            {message.content}
          </div>
      }
    </td>
    {actionColumn &&
      <td>
        {actionColumn.render(message.id)}
      </td>
    }
  </tr>
)

const getIdentityMessages = gql`
  query($id: UUID, $orderBy: String!, $status: String!) {
    inboundMessages: messages(orderBy: $orderBy, toIdentity_Id: $id, status: $status) {
      edges {
        node {
          ...Message
        }
      }
    }
    outboundMessages: messages(orderBy: $orderBy, fromIdentity_Id: $id, status: $status) {
      edges {
        node {
          ...Message
        }
      }
    }
  }
  ${fragments.message}
`

const withData = graphql(getIdentityMessages, {
  options: (ownProps) => ({
    variables: {
      id: ownProps.identity ? decodeBase64(ownProps.identity) : '',
      orderBy: ownProps.orderBy || '-created_at',
      status: ownProps.status || ''
    },
    fetchPolicy: 'network-only'
  })
})

export default withData(IdentityMessages)



// WEBPACK FOOTER //
// ./src/components/identity-messages/index.js